import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './userformpage.module.css';

// Assets
import { FaRegQuestionCircle } from "react-icons/fa";

// API
import { useUsuarioService } from '../../services/usuarioServices';
import { useProdutoUsuarioService } from '../../services/produtoUsuarioService';
import { useProdutoService } from '../../services/produtoService';

function UserFormPage() {
    const { logout } = useAuth();
    const { showToast } = useToast();
    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();

    const { listarProdutos } = useProdutoService();
    const { criarUsuario, atualizarUsuario, obterUsuarioPorId } = useUsuarioService();
    const { listarProdutosPorUsuario, assinarProduto, deletarAssinaturaProduto } = useProdutoUsuarioService();

    const { idUsuario } = useParams();
    const { pathname } = useLocation();

    const isEditing = !!idUsuario && pathname.includes('atualizar');

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        cpf: '',
        senha: '',
        isAdmin: 0
    });

    const [produtos, setProdutos] = useState([]);
    const [buscaProduto, setBuscaProduto] = useState('');
    const [sugestoesFiltradas, setSugestoesFiltradas] = useState([]);
    const [produtosAssinados, setProdutosAssinados] = useState([]);

    useEffect(() => {
        if (isEditing) {
            const carregarUsuario = async () => {
                showLoader("Carregando dados do usuário");

                const response = await obterUsuarioPorId(idUsuario);

                if (response.success) {
                    const usuario = response.data;

                    setFormData({
                        nome: usuario.nome || '',
                        email: usuario.email || '',
                        telefone: aplicarMascaraTelefone(usuario.telefone || ''),
                        cpf: aplicarMascaraCPF(usuario.cpf || ''),
                        senha: '',
                        isAdmin: usuario.isAdmin || 0
                    });
                } else {
                    showToast('Erro ao carregar usuário: ' + response.message, 3000, 'error');
                    if (response.message?.toLowerCase().includes('token')) logout();
                }

                hideLoader();
            };

            carregarUsuario();
        }
    }, [idUsuario, isEditing]);

    // 🔽 Carregar produtos e produtos assinados
    useEffect(() => {
        const carregarProdutos = async () => {
            const res = await listarProdutos();
            if (res.success) {
                setProdutos(res.data);
            } else {
                showToast("Erro ao carregar produtos", 3000, 'error');
            }
        };

        const carregarProdutosAssinados = async () => {
            if (!isEditing) return;

            const res = await listarProdutosPorUsuario(idUsuario);
            if (res.success) {
                const idsProdutos = res.produtos.map(p => p.idProduto);
                setProdutosAssinados(idsProdutos);
            } else {
                showToast("Erro ao carregar produtos assinados", 3000, 'error');
            }
        };

        carregarProdutos();
        carregarProdutosAssinados();
    }, [isEditing, idUsuario]);

    // 📱 Máscara telefone
    const aplicarMascaraTelefone = (valor) => {
        const numeros = valor.replace(/\D/g, '').slice(0, 11);
        const parte1 = numeros.slice(0, 2);
        const parte2 = numeros.slice(2, 7);
        const parte3 = numeros.slice(7, 11);

        if (numeros.length <= 2) return `(${parte1}`;
        if (numeros.length <= 7) return `(${parte1}) ${parte2}`;
        return `(${parte1}) ${parte2}-${parte3}`;
    };

    const handleTelefoneChange = (e) => {
        const valor = e.target.value;
        setFormData(prev => ({
            ...prev,
            telefone: aplicarMascaraTelefone(valor)
        }));
    };

    // 🧾 Máscara CPF
    const aplicarMascaraCPF = (valor) => {
        const numeros = valor.replace(/\D/g, '').slice(0, 11);
        const parte1 = numeros.slice(0, 3);
        const parte2 = numeros.slice(3, 6);
        const parte3 = numeros.slice(6, 9);
        const parte4 = numeros.slice(9, 11);

        if (numeros.length <= 3) return parte1;
        if (numeros.length <= 6) return `${parte1}.${parte2}`;
        if (numeros.length <= 9) return `${parte1}.${parte2}.${parte3}`;
        return `${parte1}.${parte2}.${parte3}-${parte4}`;
    };

    const handleCPFChange = (e) => {
        const valor = e.target.value;
        setFormData(prev => ({
            ...prev,
            cpf: aplicarMascaraCPF(valor)
        }));
    };

    // 🔎 Validação CPF
    const cpfValido = (cpf) => {
        cpf = cpf.replace(/\D/g, '');
        if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

        let soma = 0;
        for (let i = 0; i < 9; i++) {
            soma += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let resto = (soma * 10) % 11;
        if (resto === 10 || resto === 11) resto = 0;
        if (resto !== parseInt(cpf.charAt(9))) return false;

        soma = 0;
        for (let i = 0; i < 10; i++) {
            soma += parseInt(cpf.charAt(i)) * (11 - i);
        }
        resto = (soma * 10) % 11;
        if (resto === 10 || resto === 11) resto = 0;
        return resto === parseInt(cpf.charAt(10));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const telefoneLimpo = formData.telefone.replace(/\D/g, '');
        const cpfLimpo = formData.cpf.replace(/\D/g, '');

        if (telefoneLimpo.length !== 11) {
            showToast('O telefone deve ter 11 dígitos.', 3000, 'error');
            return;
        }

        if (!cpfValido(cpfLimpo)) {
            showToast('CPF inválido.', 3000, 'error');
            return;
        }

        showLoader(isEditing ? "Atualizando usuário" : "Criando usuário");

        const payload = {
            ...formData,
            telefone: telefoneLimpo,
            cpf: cpfLimpo
        };
        if (isEditing) payload.id = idUsuario;

        const response = isEditing
            ? await atualizarUsuario(payload)
            : await criarUsuario(payload);

        if (response.success) {
            showToast(isEditing ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!', 3000, 'success');
            navigate('/usuarios');
        } else {
            showToast(isEditing ? 'Erro ao atualizar.' : 'Erro ao criar.', 3000, 'error');
            console.error(response.message);
            if (response.message?.toLowerCase().includes('token')) logout();
        }

        hideLoader();
    };

    // 🔽 Adicionar Produto Assinado
    const handleAdicionarProduto = async (idProduto) => {
        const res = await assinarProduto(idUsuario, idProduto);
        if (res.success) {
            setProdutosAssinados(prev => [...prev, idProduto]);
            showToast("Produto adicionado à assinatura!", 3000, 'success');
        } else {
            showToast(res.message || "Erro ao adicionar produto", 3000, 'error');
        }
        setBuscaProduto('');
        setSugestoesFiltradas([]);
    };

    const handleRemoverProduto = async (idProduto) => {
        const res = await deletarAssinaturaProduto(idUsuario, idProduto);
        if (res.success) {
            setProdutosAssinados(prev => prev.filter(p => p !== idProduto));
            showToast("Produto já removido da assinatura!", 3000, 'success');
        } else {
            showToast(res.message || "Erro ao remover produto", 3000, 'error');
        }
    };

    const breadcrumbLabel = isEditing
        ? `${formData.nome || 'usuário'}`
        : undefined;

    return (
        <div className={styles.UserFormPage}>
            <div className={styles.containerTop}>
                <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idUsuario, label: breadcrumbLabel }] : []} />
                <h1 className={styles.title}>
                    {isEditing ? 'Editar Usuário' : 'Cadastro de Usuário'}
                </h1>
                {isEditing
                    ? <FriendlyPhrase page={"atualizarUsuario"} />
                    : <FriendlyPhrase page={"adicionarUsuario"} />}
            </div>

            <form onSubmit={handleSubmit} className={styles.formulario}>

                {isEditing && (
                    <div className={styles.produtoSelector}>
                        <div className={styles.divSectionTitle}>
                            <h2 className={styles.sectionTitle}>Editar produtos assinados</h2>

                            <FaRegQuestionCircle title='Não precisa editar tudo para salvar os produtos!'/>
                        </div>

                        {/* Pílulas dos produtos assinados */}
                        <div className={styles.produtoPills}>
                            {produtosAssinados.map(id => {
                                const produto = produtos.find(p => p.id === id);
                                return (
                                    <div key={id} className={styles.produtoPill}>
                                        {produto?.nome}
                                        <span
                                            className={styles.removerPill}
                                            onClick={() => handleRemoverProduto(id)}
                                        >
                                            ×
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Barra de busca */}
                        <input
                            type="text"
                            value={buscaProduto}
                            onChange={(e) => {
                                const valor = e.target.value;
                                setBuscaProduto(valor);
                                const filtrados = produtos
                                    .filter(p =>
                                        !produtosAssinados.includes(p.id) &&
                                        p.nome.toLowerCase().includes(valor.toLowerCase())
                                    );
                                setSugestoesFiltradas(filtrados);
                            }}
                            placeholder="Buscar e selecionar produtos"
                            className={styles.produtoInput}
                        />

                        {/* Lista de sugestões */}
                        {sugestoesFiltradas.length > 0 && (
                            <ul className={styles.sugestoesLista}>
                                {sugestoesFiltradas.map(prod => (
                                    <li
                                        key={prod.id}
                                        className={styles.sugestaoItem}
                                        onClick={() => handleAdicionarProduto(prod.id)}
                                    >
                                        {prod.nome}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div className={styles.generalArea}>
                    <label>Nome:
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            required
                        />
                    </label>

                    <label>E-mail:
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </label>

                    <label>Telefone:
                        <input
                            type="tel"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleTelefoneChange}
                            placeholder="(11) 91234-5678"
                            required
                        />
                    </label>

                    <label>CPF:
                        <input
                            type="text"
                            name="cpf"
                            value={formData.cpf}
                            onChange={handleCPFChange}
                            placeholder="000.000.000-00"
                            required
                        />
                    </label>

                    <label>Senha:
                        <input
                            type="password"
                            name="senha"
                            value={formData.senha}
                            onChange={handleChange}
                            placeholder={isEditing ? "Preencha para alterar" : ""}
                            required={!isEditing}
                        />
                    </label>

                    <label>Permissão:
                        <select
                            name="isAdmin"
                            value={formData.isAdmin}
                            onChange={handleChange}
                            className={styles.selectInput}
                        >
                            <option value={0}>Usuário Comum</option>
                            <option value={1}>Administrador</option>
                        </select>
                    </label>
                </div>

                <div className={styles.submitArea}>
                    <button type="submit" className={styles.submitButton}>
                        {isEditing ? 'Atualizar Usuário' : 'Salvar Usuário'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default UserFormPage;